<script setup lang="ts">
import { ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { NModal, NProgress } from 'naive-ui'
import { useEvent } from '@/composables/useEvent'
import { useNotify } from '@/composables/useNotify'
import { useUpdateLifecycle } from '@/composables/useUpdateLifecycle'
import { useUpdateStore } from '@/stores/useUpdateStore'
import { TauriEvent } from '@/types/ipc'
import { logger } from '@/utils/logger'
import { summarizeUpdateError } from '@/utils/updateError'

const updateStore = useUpdateStore()
const notify = useNotify()
const {
  version: updateVersion,
  isForcedUpdate,
  installing: updateDownloading,
  progress: updateProgress,
} = storeToRefs(updateStore)

const showUpdateDialog = ref(false)
let displayedUpdateKey = ''

useUpdateLifecycle()

function resetUpdatePresentation() {
  showUpdateDialog.value = false
  displayedUpdateKey = ''
}

watch(
  () => [updateStore.availableInfo, updateStore.manualPresentationRevision] as const,
  ([info, manualPresentationRevision]) => {
    if (!info) {
      resetUpdatePresentation()
      return
    }

    const candidateKey = `${info.version}:${!!info.isForced}:${manualPresentationRevision}`
    if (candidateKey === displayedUpdateKey) return
    displayedUpdateKey = candidateKey
    showUpdateDialog.value = true
  },
  { immediate: true },
)

useEvent(TauriEvent.UpdateAvailable, (payload) => {
  updateStore.setAvailable(payload)
})

useEvent(TauriEvent.UpdateCleared, () => {
  updateStore.clearAvailable()
  resetUpdatePresentation()
})

useEvent(TauriEvent.UpdateDownloadProgress, (payload) => {
  updateStore.setProgress(payload)
})

function getUpdateInstallErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error || '')
  if (
    message.includes('Read-only file system') ||
    message.includes('os error 30') ||
    message.includes('只读')
  ) {
    return '当前应用位于只读位置，请先将“拓客AI销冠”拖入“应用程序”文件夹后再更新'
  }
  return '更新安装失败，请重试'
}

async function handleStartUpdate() {
  try {
    await updateStore.installUpdate()
  } catch (error) {
    logger.warn('updater', 'Update install failed', summarizeUpdateError(error))
    notify.error(getUpdateInstallErrorMessage(error))
    if (isForcedUpdate.value) showUpdateDialog.value = true
  }
}
</script>

<template>
  <n-modal
    v-model:show="showUpdateDialog"
    preset="dialog"
    :type="isForcedUpdate ? 'warning' : 'info'"
    :title="isForcedUpdate ? '检测到新版本，请立即更新' : `发现新版本 v${updateVersion}`"
    :mask-closable="!isForcedUpdate"
    :close-on-esc="!isForcedUpdate"
    :closable="!isForcedUpdate"
    :show-close="!isForcedUpdate"
    positive-text="立即更新"
    :negative-text="isForcedUpdate ? undefined : '稍后'"
    :positive-button-props="{ loading: updateDownloading, disabled: updateDownloading }"
    @positive-click="
      () => {
        handleStartUpdate()
        return false
      }
    "
    @negative-click="
      () => {
        if (!isForcedUpdate) showUpdateDialog = false
      }
    "
  >
    <p v-if="isForcedUpdate">
      检测到新版本，为保证功能正常使用，请立即更新。
    </p>
    <p v-else>
      有新版本可用，建议立即更新以获得最佳体验。
    </p>
    <p
      v-if="updateDownloading"
      class="mt-2 text-sm text-gray-500"
    >
      正在下载并安装，请勿关闭应用...
    </p>
  </n-modal>

  <div
    v-if="updateDownloading"
    class="fixed top-0 right-0 left-0 z-[9999]"
  >
    <n-progress
      type="line"
      :percentage="updateProgress"
      :show-indicator="false"
      :height="3"
      status="info"
    />
  </div>
</template>
